/**
 * Shared screener-criteria evaluator used by the stock detail modal and
 * the combined chart so they can highlight the bars / annotate the
 * "days meeting criteria" count consistent with the table the user
 * came from (Big Buy at Low / Pullback in Uptrend / Strong Uptrend Dip).
 */

export type ScreenerType = 'big_buy' | 'pullback_high_volume' | 'strong_uptrend_dip';

export const SCREENER_LABEL: Record<ScreenerType, string> = {
  big_buy: 'Big Buy at Low',
  pullback_high_volume: 'Pullback in Uptrend (High Volume)',
  strong_uptrend_dip: 'Shallow Dip in Strong Uptrend',
};

/** SMA-window keys actually drawn / referenced by each screener. */
export const SCREENER_SMA_WINDOWS: Record<ScreenerType, ('dma_20' | 'dma_50' | 'dma_200')[]> = {
  big_buy: ['dma_200'],
  pullback_high_volume: ['dma_50', 'dma_200'],
  strong_uptrend_dip: ['dma_20', 'dma_50', 'dma_200'],
};

export interface ScreenerEvalDay {
  close_price: number;
  traded_qty: number;
  delivery_qty: number;
  dma_20?: number | null;
  dma_50?: number | null;
  dma_200?: number | null;
  avg30_tq?: number | null;
}

const isFiniteNumber = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/**
 * SMA-comparison gate of a screener for a single day. The caller is
 * responsible for combining this with the delivery-percentage and the
 * delivery-amount range gates that the modal already evaluates.
 *
 * - big_buy:               close < SMA200
 * - pullback_high_volume:  close < SMA50 ∧ close > SMA200 ∧ traded_qty > 30D avg traded_qty
 * - strong_uptrend_dip:    close < SMA20 ∧ close > SMA50 ∧ close > SMA200
 */
export function meetsSmaGate(d: ScreenerEvalDay, screener: ScreenerType): boolean {
  switch (screener) {
    case 'big_buy':
      return isFiniteNumber(d.dma_200) && d.close_price < d.dma_200;
    case 'pullback_high_volume':
      return (
        isFiniteNumber(d.dma_50) &&
        isFiniteNumber(d.dma_200) &&
        isFiniteNumber(d.avg30_tq) &&
        d.close_price < d.dma_50 &&
        d.close_price > d.dma_200 &&
        d.traded_qty > d.avg30_tq
      );
    case 'strong_uptrend_dip':
      return (
        isFiniteNumber(d.dma_20) &&
        isFiniteNumber(d.dma_50) &&
        isFiniteNumber(d.dma_200) &&
        d.close_price < d.dma_20 &&
        d.close_price > d.dma_50 &&
        d.close_price > d.dma_200
      );
  }
}
